import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Settings, PoundSterling, Headphones, TrendingUp, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { services } from '../data/mock';
import SEO from '../components/ui/SEO';

gsap.registerPlugin(ScrollTrigger);

const iconMap = { Settings, PoundSterling, Headphones, TrendingUp };

const ServicesPage = () => {
  const pageRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => element.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      gsap.fromTo('.services-hero',
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power3.out' }
      );

      document.querySelectorAll('.service-section').forEach((section) => {
        gsap.fromTo(section.querySelector('.service-content'),
          { opacity: 0, y: 50 },
          {
            opacity: 1, y: 0, duration: 0.7, ease: 'power3.out',
            scrollTrigger: { trigger: section, start: 'top 80%' },
          }
        );

        gsap.fromTo(section.querySelectorAll('.benefit-item'),
          { opacity: 0, x: -20 },
          {
            opacity: 1, x: 0, duration: 0.4, stagger: 0.1, ease: 'power2.out',
            scrollTrigger: { trigger: section, start: 'top 70%' },
          }
        );
      });
    });

    return () => ctx.revert();
  }, []);

  return (
    <>
      <SEO
        title="Business Support Services"
        description="VISO Connect offers customer service, bookkeeping, virtual assistance, and data management solutions for growing businesses. Expert teams, proven processes."
        canonical="https://visoconnect.in/services"
      />
      <div ref={pageRef} className="overflow-hidden bg-[#0a0a0f]">
        {/* Hero */}
        <section className="pt-32 pb-20 bg-[#0a0a0f] relative">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-indigo-500/10 rounded-full blur-[150px] pointer-events-none" />

          <div className="relative max-w-4xl mx-auto px-6 lg:px-8">
            <div className="services-hero text-center">
              <span className="text-indigo-400 font-semibold text-sm tracking-widest uppercase mb-4 block">
                Our Services
              </span>
              <h1 className="text-white mb-6" style={{
                fontSize: 'clamp(2.5rem, 6vw, 3.5rem)',
                fontWeight: 800,
                lineHeight: 1.1,
                letterSpacing: '-0.03em'
              }}>
                Business Support{' '}
                <span className="gradient-text">Solutions</span>
              </h1>
              <p className="text-slate-400 mx-auto" style={{ maxWidth: '520px', fontSize: '1.1rem' }}>
                Expert teams and proven processes for reliable operational support.
              </p>
            </div>
          </div>
        </section>

        {/* Services */}
        {services.map((service, index) => {
          const IconComponent = iconMap[service.icon];
          const isEven = index % 2 === 0;

          return (
            <section
              key={service.id}
              id={service.id}
              className={`service-section py-24 scroll-mt-24 ${isEven ? 'bg-[#111118]' : 'bg-[#0a0a0f]'}`}
            >
              <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className={`service-content grid lg:grid-cols-2 gap-16 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}>
                  {/* Content */}
                  <div className={isEven ? '' : 'lg:order-2'}>
                    <div className="w-16 h-16 bg-gradient-to-br from-indigo-500/20 to-cyan-500/20 rounded-2xl flex items-center justify-center mb-6">
                      {IconComponent && <IconComponent className="text-indigo-400" size={32} />}
                    </div>
                    <h2 className="text-white mb-4" style={{
                      fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                      fontWeight: 700,
                      letterSpacing: '-0.02em'
                    }}>
                      {service.title}
                    </h2>
                    <p className="text-slate-400 leading-relaxed mb-8" style={{ maxWidth: '480px' }}>
                      {service.fullDescription}
                    </p>

                    {/* Benefits */}
                    <div className="space-y-3 mb-8">
                      {service.benefits.map((benefit, i) => (
                        <div key={i} className="benefit-item flex items-start gap-3">
                          <div className="w-5 h-5 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="text-emerald-400" size={12} strokeWidth={3} />
                          </div>
                          <span className="text-slate-300 text-sm">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    <Link to="/contact">
                      <Button className="btn-glow bg-gradient-to-r from-indigo-500 to-cyan-500 text-white rounded-xl border-0">
                        Enquire
                        <ArrowRight className="ml-2" size={16} />
                      </Button>
                    </Link>
                  </div>

                  {/* Use Cases */}
                  <div className={isEven ? '' : 'lg:order-1'}>
                    <div className="card-dark p-8">
                      <h3 className="text-lg font-bold text-white mb-6">Use Cases</h3>
                      <div className="space-y-3">
                        {service.useCases.map((useCase, i) => (
                          <div key={i} className="flex items-center gap-4 p-4 bg-white/5 rounded-xl hover:bg-white/10 transition-colors">
                            <div className="w-8 h-8 bg-gradient-to-br from-indigo-500/30 to-cyan-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                              <span className="text-indigo-300 font-semibold text-sm">{i + 1}</span>
                            </div>
                            <span className="text-slate-300 text-sm font-medium">{useCase}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        {/* CTA */}
        <section className="py-24 bg-gradient-to-r from-indigo-600 to-cyan-600 relative">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }} />
          </div>
          <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-white mb-5" style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: 700,
              letterSpacing: '-0.02em'
            }}>
              Need a tailored solution?
            </h2>
            <p className="text-white/80 mb-10" style={{ maxWidth: '420px', margin: '0 auto 2.5rem' }}>
              Let's discuss your specific requirements.
            </p>
            <Link to="/contact">
              <Button className="bg-white text-slate-900 hover:bg-slate-100 px-8 py-6 text-base font-semibold rounded-xl">
                Contact Us
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default ServicesPage;
